import { ArticleStackCards } from "@components/Cards/Layout/ArticleStackCards";
import List from "@elements/List/List";
import { ListItem } from "@elements/List/ListItem/ListItem";
import Heading from "@elements/Typography/Heading";
import {
  DEFAULT_COLOR,
  SubmenuItem,
  SUBSECTION_MENUS,
} from "@helper/sectionConstants";
import { ProcessedArticleData } from "@transformer/useOsResponse";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router";
import { twMerge } from "tailwind-merge";

export const RecentArticlesSection = ({
  recentArticles,
  sectionName,
  sectionColor,
  enableSubmenus = true,
  rootClassName,
  refMedium,
}: {
  recentArticles: ProcessedArticleData[];
  sectionName?: string;
  sectionColor?: string;
  enableSubmenus?: boolean;
  rootClassName?: string;
  refMedium?: string;
}) => {
  const sectionSubmenus =
    sectionName &&
    sectionName in SUBSECTION_MENUS &&
    SUBSECTION_MENUS[sectionName as keyof typeof SUBSECTION_MENUS];

  const currentUrl = useLocation().pathname;
  const [isScrolled, setIsScrolled] = useState<boolean>(false);

  useEffect(() => {
    const handleScroll = () => {
      const sectionTitleEle = document.querySelector(".section-title");
      if (!sectionTitleEle) return;

      const titleScollingWrpper = sectionTitleEle.getBoundingClientRect();
      if (titleScollingWrpper.top <= 60) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <div className={twMerge("mt-xxs inline-block w-full", rootClassName)}>
      {sectionName && currentUrl.split("/").filter(Boolean).length === 1 && (
        <Heading
          className="section-title text-grey-200 text-40 lg:text-100 font-bold text-center uppercase font-primary w-max mx-auto border-t-[3px] border-solid"
          style={{
            borderColor: sectionColor ? sectionColor : DEFAULT_COLOR,
          }}
        >
          {sectionName}
        </Heading>
      )}

      {enableSubmenus && sectionName && sectionSubmenus && (
        <>
          {currentUrl.split("/").filter(Boolean).length > 1 && (
            <div
              className="section-title text-grey-200 text-40 xmd:text-100 font-bold text-center uppercase font-primary w-max mx-auto border-t-[3px] border-solid"
              style={{
                borderColor: sectionColor ? sectionColor : DEFAULT_COLOR,
              }}
            >
              {sectionName}
            </div>
          )}

          <div
            className={twMerge(
              "inline z-[8] -translate-y-full",
              isScrolled &&
                "fixed translate-y-0 top-xxl bg-white xmd:mx-auto w-full max-w-[100vw] left-0 border-t-2 border-solid transition-transform duration-1000 ease-in-out",
            )}
            style={{
              borderColor: sectionColor ? sectionColor : DEFAULT_COLOR,
            }}
          >
            <List
              testId={`${sectionName.toLowerCase()}-sectionSubmenus-list`}
              className={twMerge(
                "flex gap-1 justify-center",
                isScrolled &&
                  "max-w-[1440px] m-auto w-full p-0 md:px-4 lg:px-[121px] justify-center lg:justify-start",
              )}
            >
              {sectionSubmenus.map((item: SubmenuItem, index: number) => (
                <ListItem
                  key={`${sectionName.toLowerCase()}-sectionSubmenus-item-${index}`}
                  testId={`${sectionName.toLowerCase()}-sectionSubmenus-item-${index}`}
                  className={twMerge(
                    "px-5 pb-2.5 pt-xxsm opacity-70 hover:opacity-100",
                    currentUrl == item.url && "opacity-100",
                    isScrolled && index == 0 && "xmd:pl-0",
                  )}
                >
                  <Link
                    reloadDocument
                    to={item.url}
                    target={item.newTab ? "_blank" : "_self"}
                    rel={item.newTab ? "noreferrer" : undefined}
                    className="text-grey-500 hover:text-grey-200 font-opensans text-sm xmd:text-base font-bold leading-5 xmd:leading-6"
                  >
                    {currentUrl === item.url && item.title !== "Latest" ? (
                      <Heading className="text-grey-200">{item.title}</Heading>
                    ) : (
                      item.title
                    )}
                  </Link>
                </ListItem>
              ))}
            </List>
          </div>
        </>
      )}

      <ArticleStackCards
        articles={recentArticles}
        sectionColor={sectionColor}
        refMedium={refMedium}
        rootClassName={twMerge(
          "xmd:pb-0",
          !enableSubmenus ? "xmd:pt-0" : "xmd:pt-4",
        )}
      />
    </div>
  );
};
